/**
 * Fetching text assets that may be LMCENC-encrypted.
 *
 * One choke point for every playlist and every VTT the wrapper reads, so the
 * encryption story is uniform and the failure modes are typed.
 */
import { isEncryptedPayload, isPlaylistText, isVttText, } from '@luminary-media-converter/hls-core';
import { decryptLmcenc } from './decrypt.js';
/** A typed pipeline failure, convertible to a {@link PlayerError}. */
export class PipelineError extends Error {
    code;
    fatal;
    /**
     * True for HTTP 404 and 403 — S3 answers 403 for a missing key when the
     * caller has no list permission, so both mean "not published (yet)". The
     * coming-soon poller keys off this.
     */
    missing;
    url;
    /** The HTTP status the server answered with, for a non-2xx response. */
    status;
    constructor(code, message, options = {}) {
        super(message);
        this.name = 'PipelineError';
        this.code = code;
        this.fatal = true;
        this.missing = options.missing ?? false;
        this.url = options.url;
        this.status = options.status;
        if (options.cause !== undefined)
            this.cause = options.cause;
    }
    toPlayerError() {
        return {
            code: this.code,
            fatal: this.fatal,
            message: this.message,
            cause: this.cause,
        };
    }
}
/** True when `error` means "the resource is not there (yet)". */
export function isMissing(error) {
    return error instanceof PipelineError && error.missing;
}
/** Normalize any thrown value into a presentable {@link PlayerError}. */
export function toPlayerError(error) {
    if (error instanceof PipelineError)
        return error.toPlayerError();
    // Adapters throw their own "this browser cannot play munged HLS" error and
    // cannot import PipelineError (it is pipeline-internal), so they mark it
    // with the code instead. Without this the error arrives as 'unknown' and
    // `errorUnsupportedBrowser` is a message no player can ever show.
    const code = error instanceof Error &&
        error.code === 'unsupported-browser'
        ? 'unsupported-browser'
        : 'unknown';
    return {
        code,
        fatal: true,
        message: error instanceof Error ? error.message : String(error),
        cause: error,
    };
}
/** Raw bytes of a URL, with 404/403 surfaced as `missing`. */
export async function fetchBytes(url, options) {
    let response;
    try {
        response = await options.fetchImpl(url, {
            signal: options.signal,
            ...(options.httpCache ? { cache: options.httpCache } : {}),
        });
    }
    catch (cause) {
        throw new PipelineError('fetch-failed', `Could not fetch ${url}`, {
            cause,
            url,
        });
    }
    if (!response.ok) {
        const missing = response.status === 404 || response.status === 403;
        throw new PipelineError('fetch-failed', `HTTP ${response.status} for ${url}`, { missing, url, status: response.status });
    }
    return new Uint8Array(await response.arrayBuffer());
}
/** Decode already-fetched bytes. Exposed for tests and for byte-level probes. */
export async function decodeMaybeEncrypted(bytes, url, options) {
    const expect = options.expect ?? 'any';
    if (isEncryptedPayload(bytes)) {
        if (!options.keyHex) {
            throw new PipelineError('key-required', `${url} is LMCENC-encrypted but no session key was supplied`, { url });
        }
        let plain;
        try {
            plain = await decryptLmcenc(bytes, options.keyHex, options.subtle);
        }
        catch (cause) {
            throw new PipelineError('decrypt-failed', `Could not decrypt ${url}`, { cause, url });
        }
        if (!matchesExpectation(plain, expect)) {
            // Padding happened to validate but the plaintext is nonsense —
            // almost always the wrong key.
            throw new PipelineError('decrypt-failed', `Decrypted ${url} is not a ${expect === 'vtt' ? 'WebVTT file' : 'playlist'}`, { url });
        }
        return { url, text: decodeUtf8(plain), wasEncrypted: true };
    }
    if (!matchesExpectation(bytes, expect)) {
        // Say what arrived, not only that it was wrong: the URL alone cannot
        // distinguish an error page, an empty body, and a stale cache entry,
        // and each of those is a different bug.
        const head = Array.from(bytes.subarray(0, 48))
            .map((b) => (b >= 0x20 && b < 0x7f ? String.fromCharCode(b) : `\\x${b.toString(16).padStart(2, '0')}`))
            .join('');
        throw new PipelineError('invalid-content', `${url} is neither LMCENC nor a recognizable ${expect === 'vtt' ? 'WebVTT file' : 'playlist'} ` +
            `(${bytes.length} bytes, starts: "${head}")`, { url });
    }
    return { url, text: decodeUtf8(bytes), wasEncrypted: false };
}
/**
 * Fetch a text asset, transparently decrypting LMCENC payloads.
 *
 * Failure modes map straight onto {@link PlayerError} codes:
 * `fetch-failed` (transport / non-2xx, `missing` for 404+403),
 * `key-required` (LMCENC with no key), `decrypt-failed` (wrong key),
 * `invalid-content` (neither LMCENC nor the expected plaintext).
 */
export async function fetchMaybeEncrypted(url, options) {
    const bytes = await fetchBytes(url, options);
    return decodeMaybeEncrypted(bytes, url, options);
}
function matchesExpectation(bytes, expect) {
    if (expect === 'playlist')
        return isPlaylistText(bytes);
    if (expect === 'vtt')
        return isVttText(bytes);
    return isPlaylistText(bytes) || isVttText(bytes);
}
/**
 * One decoder for every asset. A non-streaming `decode()` keeps no state
 * between calls, so sharing it is safe; made on first use rather than at import
 * so that loading the module asks nothing of the runtime.
 */
let utf8;
function decodeUtf8(bytes) {
    utf8 ??= new TextDecoder('utf-8');
    return utf8.decode(bytes);
}
//# sourceMappingURL=fetch.js.map